// src/pages/services/MarketValuations.jsx pour un client diiférenet du composant FamilyOffice
import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, TrendingUp, TrendingDown, Minus, AlertCircle, Search } from "lucide-react";
import { supabase } from "../../lib/supabaseClient";

export default function MarketValuations() {
  const [selectedFamily, setSelectedFamily] = useState("");
  const [families, setFamilies] = useState([]);
  const [artworks, setArtworks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  // Charger les familles - une seule fois au montage
  useEffect(() => {
    const loadFamilies = async () => {
      try {
        const { data: familiesData, error } = await supabase
          .from("families")
          .select("id, label")
          .eq("status", "active")
          .order("label");

        if (error) throw error;
        
        setFamilies(familiesData || []);
        if (familiesData && familiesData.length > 0) {
          setSelectedFamily(familiesData[0].id);
        }
      } catch (error) {
        console.error("Erreur chargement familles:", error);
      }
    };

    loadFamilies();
  }, []);

  // Charger les œuvres - mémorisé avec useCallback
  const loadArtworks = useCallback(async () => {
    if (!selectedFamily) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      // On récupère d'abord les collections liées à la famille
      const { data: collections, error: collError } = await supabase
        .from("collections")
        .select("id")
        .eq("family_id", selectedFamily);

      if (collError) {
        console.log("Pas de table collections, essai avec client_id...");
        // Si pas de table collections, on essaie avec client_id directement
        const { data, error } = await supabase
          .from("artworks")
          .select("id, title, artist, year, price, value, estimated_value, current_estimated_value, last_valuation_date, last_estimation_date, image_url, acquisition_price")
          .eq("client_id", selectedFamily)
          .order("title");

        if (error) throw error;
        setArtworks(data || []);
        setLoading(false);
        return;
      }

      const collectionIds = collections?.map(c => c.id) || [];

      if (collectionIds.length === 0) {
        setArtworks([]);
        setLoading(false);
        return;
      }

      // Puis on récupère les œuvres de ces collections
      const { data, error } = await supabase
        .from("artworks")
        .select("id, title, artist, year, price, value, estimated_value, current_estimated_value, last_valuation_date, last_estimation_date, image_url, acquisition_price")
        .in("collection_id", collectionIds)
        .order("title");

      if (error) throw error;
      
      setArtworks(data || []);
    } catch (error) {
      console.error("Erreur chargement œuvres:", error);
      setArtworks([]);
    } finally {
      setLoading(false);
    }
  }, [selectedFamily]);

  // Charger les œuvres quand la famille change
  useEffect(() => {
    loadArtworks();
  }, [loadArtworks]);

  // Calculer la variation de valeur
  const getValueChange = (artwork) => {
    const purchasePrice = artwork.price || artwork.acquisition_price || 0;
    const currentValue = artwork.value || artwork.estimated_value || artwork.current_estimated_value || 0;
    
    if (!purchasePrice || !currentValue || purchasePrice === 0) return null;
    
    const change = ((currentValue - purchasePrice) / purchasePrice) * 100;
    return change;
  };

  // Filtrer par recherche
  const filteredArtworks = artworks.filter(artwork =>
    artwork.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    artwork.artist?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Calculer les statistiques globales
  const totalValue = artworks.reduce((sum, art) => {
    const val = art.value || art.estimated_value || art.current_estimated_value || 0;
    return sum + val;
  }, 0);
  
  const totalPurchase = artworks.reduce((sum, art) => {
    const price = art.price || art.acquisition_price || 0;
    return sum + price;
  }, 0);
  
  const globalChange = totalPurchase > 0 ? ((totalValue - totalPurchase) / totalPurchase) * 100 : 0;

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      {/* Header */}
      <div className="bg-white border-b border-[#E8E8E8]">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <Link 
            to="/family-office/dashboard" 
            className="inline-flex items-center gap-2 text-sm text-[#666] hover:text-[#1A1A1A] transition-colors mb-6"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour au tableau de bord
          </Link>

          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-serif text-[#1A1A1A] mb-2">
                Valorisations de Marché
              </h1>
              <p className="text-lg text-[#666] font-light">
                Estimations actualisées de votre collection
              </p>
            </div>

            {/* Sélecteur de famille */}
            {families.length > 0 && (
              <select
                value={selectedFamily}
                onChange={(e) => setSelectedFamily(e.target.value)}
                className="px-4 py-2.5 border border-[#E8E8E8] rounded-lg bg-white text-[#1A1A1A] font-light focus:outline-none focus:ring-2 focus:ring-[#1A1A1A] focus:border-transparent"
              >
                {families.map(family => (
                  <option key={family.id} value={family.id}>
                    {family.label}
                  </option>
                ))}
              </select>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-10">
        {/* Statistiques globales */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
          <div className="bg-white rounded-xl border border-[#E8E8E8] p-6">
            <p className="text-sm text-[#666] mb-2 font-light">Valeur Totale Actuelle</p>
            <p className="text-3xl font-serif text-[#1A1A1A]">
              {totalValue.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 })}
            </p>
          </div>

          <div className="bg-white rounded-xl border border-[#E8E8E8] p-6">
            <p className="text-sm text-[#666] mb-2 font-light">Prix d'Achat Total</p>
            <p className="text-3xl font-serif text-[#1A1A1A]">
              {totalPurchase.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 })}
            </p>
          </div>

          <div className="bg-white rounded-xl border border-[#E8E8E8] p-6">
            <p className="text-sm text-[#666] mb-2 font-light">Variation Globale</p>
            <div className="flex items-center gap-2">
              <p className={`text-3xl font-serif ${globalChange >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                {globalChange >= 0 ? '+' : ''}{globalChange.toFixed(1)}%
              </p>
              {globalChange > 0 && <TrendingUp className="w-6 h-6 text-emerald-600" />}
              {globalChange < 0 && <TrendingDown className="w-6 h-6 text-red-600" />}
              {globalChange === 0 && <Minus className="w-6 h-6 text-[#666]" />}
            </div>
          </div>
        </div>

        {/* Barre de recherche */}
        <div className="mb-8">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#999]" />
            <input
              type="text"
              placeholder="Rechercher une œuvre ou un artiste..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3.5 border border-[#E8E8E8] rounded-lg bg-white text-[#1A1A1A] font-light focus:outline-none focus:ring-2 focus:ring-[#1A1A1A] focus:border-transparent"
            />
          </div>
        </div>

        {/* Liste des œuvres */}
        {loading ? (
          <div className="text-center py-20">
            <div className="inline-block w-8 h-8 border-2 border-[#E8E8E8] border-t-[#1A1A1A] rounded-full animate-spin"></div>
            <p className="text-[#666] mt-4 font-light">Chargement des valorisations...</p>
          </div>
        ) : filteredArtworks.length === 0 ? (
          <div className="bg-white rounded-xl border border-[#E8E8E8] p-12 text-center">
            <AlertCircle className="w-12 h-12 text-[#999] mx-auto mb-4" />
            <p className="text-[#666] font-light">
              {searchTerm ? "Aucune œuvre ne correspond à votre recherche" : "Aucune œuvre dans cette collection"}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4">
            {filteredArtworks.map((artwork) => {
              const change = getValueChange(artwork);
              const purchasePrice = artwork.price || artwork.acquisition_price || 0;
              const currentValue = artwork.value || artwork.estimated_value || artwork.current_estimated_value || 0;
              const lastDate = artwork.last_valuation_date || artwork.last_estimation_date;
              
              return (
                <div key={artwork.id} className="bg-white rounded-xl border border-[#E8E8E8] p-6 hover:shadow-md transition-shadow">
                  <div className="flex items-start gap-6">
                    {/* Image */}
                    {artwork.image_url ? (
                      <img 
                        src={artwork.image_url} 
                        alt={artwork.title}
                        className="w-24 h-24 object-cover rounded-lg flex-shrink-0"
                      />
                    ) : (
                      <div className="w-24 h-24 bg-[#F5F5F5] rounded-lg flex items-center justify-center flex-shrink-0">
                        <span className="text-[#999] text-xs">Pas d'image</span>
                      </div>
                    )}

                    {/* Informations */}
                    <div className="flex-1 min-w-0">
                      <h3 className="text-xl font-serif text-[#1A1A1A] mb-1 truncate">
                        {artwork.title || "Sans titre"}
                      </h3>
                      <p className="text-sm text-[#666] mb-4 font-light">
                        {artwork.artist || "Artiste inconnu"} {artwork.year && `• ${artwork.year}`}
                      </p>
                      
                      <div className="grid grid-cols-3 gap-6">
                        <div>
                          <p className="text-xs text-[#999] mb-1 font-light">Prix d'achat</p>
                          <p className="text-lg font-serif text-[#1A1A1A]">
                            {purchasePrice > 0 ? 
                              purchasePrice.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }) 
                              : '—'}
                          </p>
                        </div>

                        <div>
                          <p className="text-xs text-[#999] mb-1 font-light">Valeur actuelle</p>
                          <p className="text-lg font-serif text-[#1A1A1A]">
                            {currentValue > 0 ? 
                              currentValue.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }) 
                              : '—'}
                          </p>
                        </div>

                        <div>
                          <p className="text-xs text-[#999] mb-1 font-light">Variation</p>
                          {change !== null ? (
                            <div className="flex items-center gap-2">
                              <p className={`text-lg font-serif ${change >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                                {change >= 0 ? '+' : ''}{change.toFixed(1)}%
                              </p>
                              {change > 0 && <TrendingUp className="w-4 h-4 text-emerald-600" />}
                              {change < 0 && <TrendingDown className="w-4 h-4 text-red-600" />}
                              {change === 0 && <Minus className="w-4 h-4 text-[#666]" />}
                            </div>
                          ) : (
                            <p className="text-lg font-serif text-[#999]">—</p>
                          )}
                        </div>
                      </div>

                      {lastDate && (
                        <p className="text-xs text-[#999] mt-3 font-light">
                          Dernière estimation : {new Date(lastDate).toLocaleDateString('fr-FR')}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Note méthodologique */}
        <div className="mt-10 bg-amber-50 border border-amber-200 rounded-xl p-6">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-serif text-[#1A1A1A] mb-2">Note sur les estimations</h4>
              <p className="text-sm text-[#666] font-light leading-relaxed">
                Ces estimations sont calculées à partir des données de marché disponibles et des algorithmes d'analyse d'Artkiv. 
                Elles constituent des indicateurs de tendance mais ne remplacent pas une expertise formelle par un professionnel certifié. 
                Pour une valorisation officielle (assurance, succession, vente), nous recommandons de faire appel à un expert agréé.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}